import { Mountain, User, Route, Calendar, Map, Star, Info, Home, Phone, Mail, Globe, Music, Camera, ShoppingBag, Utensils, Trees, Building, Bike, Fish, Sunset, type LucideIcon } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { WeatherWidget } from "@/components/WeatherWidget";
import { NavigationCard } from "@/components/NavigationCard";
import { FeatureImage } from "@/components/FeatureImage";
import { ActionButton } from "@/components/ActionButton";
import { WebViewModal } from "@/components/WebViewModal";
import { useURLConfig } from "@/hooks/useURLConfig";
import { HealthCheck } from "@/components/HealthCheck";
import { usePrefetch } from "@/hooks/usePrefetch";
import { StatusBar } from "@/components/StatusBar";
import type { NavButtonIcon } from "@/components/MunicipalityForm";
import { isUrlBlocked } from "@/lib/blockedDomains";

const ICON_MAP: Record<NavButtonIcon | string, LucideIcon> = {
  Mountain, Route, User, Calendar, Map, Star, Info,
  Home, Phone, Mail, Globe, Music, Camera, ShoppingBag,
  Utensils, Trees, Building, Bike, Fish, Sunset,
};

// Extrae el dominio de una URL de forma segura
const extractDomain = (url: string): string | null => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
};

// Valida si una URL está en la lista de dominios permitidos
const isUrlAllowed = (url: string, allowedDomains: string[]): boolean => {
  try {
    const domain = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    return allowedDomains.some(allowed =>
      domain === allowed || domain.endsWith('.' + allowed)
    );
  } catch {
    return false;
  }
};

const Index = () => {
  const config = useURLConfig();
  const navigate = useNavigate();

  // Redirect to auth if no municipality parameters are present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasMunicipalityParams = params.has('city') || params.has('municipality') || params.has('lat');
    
    if (!hasMunicipalityParams) {
      navigate('/auth');
    }
  }, [navigate]);
  
  // Solo se permite navegar a destinos que estén realmente visibles y
  // configurados en el tótem. Los recursos de esas páginas pueden proceder de
  // otros hosts, pero nunca se convierten en destinos de navegación válidos.
  const allowedDomains = useMemo(() => {
    const domains = new Set<string>();
    const addUrl = (candidate?: string) => {
      if (!candidate) return;
      const domain = extractDomain(candidate);
      if (domain) domains.add(domain);
    };

    config.heroes.forEach(hero => addUrl(hero.clickUrl));

    if (config.navButtons.length > 0) {
      config.navButtons.forEach(btn => {
        if (btn.action === 'external_url') addUrl(btn.url);
      });
    } else {
      addUrl('https://totems.sierranortemadrid.org/events');
    }

    if (config.bottomButtons.length > 0) {
      config.bottomButtons.forEach(btn => {
        if (btn.action === 'external_url') addUrl(btn.url);
      });
    } else {
      addUrl(config.newsletterUrl || 'https://totems.sierranortemadrid.org/newsletter');
    }

    return Array.from(domains);
  }, [config.bottomButtons, config.heroes, config.navButtons, config.newsletterUrl]);

  const [webViewState, setWebViewState] = useState<{
    isOpen: boolean;
    url: string;
    title?: string;
  }>({
    isOpen: false,
    url: '',
    title: undefined
  });

  // usePrefetch({
  //   urls: [
  //     'https://totems.sierranortemadrid.org/enjoy',
  //     config.carpetaniaUrl,
  //     'https://totems.sierranortemadrid.org/events',
  //     'https://totems.sierranortemadrid.org/newsletter',
  //   ],
  //   delay: 3000,
  //   enabled: true
  // });

  useEffect(() => {
    const handleCloseWebView = () => {
      setWebViewState({ isOpen: false, url: '', title: undefined });
    };
    
    window.addEventListener('closeWebView', handleCloseWebView);
    
    return () => {
      window.removeEventListener('closeWebView', handleCloseWebView);
    };
  }, []);

  const openWebView = (url: string, title?: string) => {
    if (isUrlBlocked(url, config.proxyBlockedDomains)) {
      console.log('⛔ Dominio bloqueado para este municipio:', url);
      return;
    }

    // Validar si la URL está permitida
    if (!isUrlAllowed(url, allowedDomains)) {
      console.log('⛔ URL no permitida, ignorando:', url);
      return; // No hacer nada, quedarse donde está
    }
    setWebViewState({ isOpen: true, url, title });
    window.dispatchEvent(new Event('webViewOpen'));
  };

  const closeWebView = useCallback(() => {
    setWebViewState({ isOpen: false, url: '', title: undefined });
    window.dispatchEvent(new Event('webViewClose'));
  }, []);
  
  return (
    <div className="page-wrapper">
      <HealthCheck />
      <StatusBar />
      <WeatherWidget
        location={config.city} 
        lat={config.lat} 
        lon={config.lon}
        apiKey={config.apiKey}
      />
      
      <div className="container">
        <div className="buttons-grid-top">
          {config.navButtons && config.navButtons.length > 0 ? (
            config.navButtons.map((btn) => {
              const IconComponent = ICON_MAP[btn.icon] ?? Mountain;
              const handleClick = () => {
                if (btn.action === 'internal_page' && btn.page) {
                  navigate(`${btn.page}${window.location.search}`);
                } else if (btn.action === 'external_url' && btn.url) {
                  openWebView(btn.url, btn.label);
                }
              };
              return (
                <NavigationCard
                  key={btn.id}
                  icon={IconComponent}
                  title={btn.label}
                  onClick={handleClick}
                />
              );
            })
          ) : (
            <>
              <NavigationCard
                icon={Route}
                title="Rutas"
                onClick={() => navigate(`/rutas${window.location.search}`)}
              />
              <NavigationCard
                icon={Map}
                title="Mapa interactivo"
                onClick={() => navigate(`/map${window.location.search}`)}
              />
              <NavigationCard
                icon={Calendar}
                title="Agenda"
                onClick={() => navigate(`/agenda${window.location.search}`)}
              />
              <NavigationCard
                icon={Camera}
                title="Visitas 360"
                onClick={() => navigate(`/visitas-360${window.location.search}`)}
              />
            </>
          )}
        </div>
        
        <FeatureImage 
          municipalityId={config.municipalityId}
          alt={`Vista histórica de ${config.municipality}`}
          label={`Descubre ${config.municipality}`}
          fallbackImage={config.heroes[0]?.imageUrl}
          onClick={config.heroes[0]?.clickUrl
            ? () => openWebView(config.heroes[0].clickUrl, config.heroes[0].name)
            : undefined}
        />
        
        <div className="buttons-grid-bottom">
          {config.bottomButtons && config.bottomButtons.length > 0 ? (
            config.bottomButtons.map((btn) => {
              const IconComponent = ICON_MAP[btn.icon] ?? Globe;
              const handleClick = () => {
                if (btn.action === 'internal_page' && btn.page) {
                  navigate(`${btn.page}${window.location.search}`);
                } else if (btn.action === 'external_url' && btn.url) {
                  openWebView(btn.url, btn.label);
                }
              };
              return (
                <ActionButton key={btn.id} icon={IconComponent} onClick={handleClick}>
                  {btn.label}
                </ActionButton>
              );
            })
          ) : (
            <>
              <ActionButton onClick={() => openWebView(config.newsletterUrl || "https://totems.sierranortemadrid.org/newsletter", "Boletín")}>
                Accede a la web del municipio
              </ActionButton>
              <ActionButton onClick={() => openWebView(config.newsletterUrl || "https://totems.sierranortemadrid.org/newsletter", "Boletín")}>
                ¿Quieres recibir nuestra agenda de actividades?
              </ActionButton>
            </>
          )}
        </div>
      </div>

      <WebViewModal 
        isOpen={webViewState.isOpen}
        onClose={closeWebView}
        url={webViewState.url}
        title={webViewState.title}
        blockedDomains={config.proxyBlockedDomains}
        allowedDomains={allowedDomains}
      />
    </div>
  );
};
export default Index;
